import { Helmet } from 'react-helmet';
import { Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import moment from 'moment';

import AnimatedRoutes, { absolute } from './components/AnimatedRoutes';
import { Context as SocketContext } from '../../components/Websocket';
import { navigate } from '../../helpers';
import { update as updateAppState } from '../../ducks/app';
import Download from './Download';
import Event from './Event';
import Events from './Events';
import Link from '../../components/Link';
import Me from './Me';
import Offline from './components/Offline';
import RavenErrorBoundary from '../../components/RavenErrorBoundary';
import appIcon from '../../assets/touch-icon-iphone.png';
import launchImg from '../../assets/launch.png';
import metaImage from '../../mobile/res/brand/ios/icon-1024.png';
import styles from './app.scss';
import withUser from '../../components/withUser';

const AnimatedMe = absolute(Me, 501);
const AnimatedEvent = absolute(Event);

const propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    listen: PropTypes.func.isRequired,
  }).isRequired,
  startRoute: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  transparent: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

const description = 'Events. Activities. Maps. Tickets. Streams. All your DreamHack needs in one place.';

if (typeof window !== 'undefined') {
  moment.locale(
    (window.navigator.languages && window.navigator.languages[0])
      || window.navigator.userLanguage
      || window.navigator.language,
  );
}

class App extends React.Component {
  constructor(props) {
    super(props);
    const { startRoute, history, dispatch } = this.props;
    if (startRoute) {
      navigate(history, startRoute);
    }

    history.listen((historyObj) => {
      dispatch(updateAppState({
        history: historyObj,
      }));
    });
  }

  componentDidMount() {
    document.body.className = 'transparent';
  }

  componentWillUnmount() {
    document.body.className = '';
    this.context.leave('offers');
  }

  render() {
    const { location, match, transparent } = this.props;

    return (
      <div
        className={classnames(styles.container, {
          [styles.transparent]: transparent,
        })}
      >
        <div
          className={classnames(styles.body, {
            [styles.transparent]: transparent,
          })}
        >
          <Helmet>
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
            <meta name="viewport" content="viewport-fit=cover, initial-scale=1, maximum-scale=1, user-scalable=no, width=device-width" />
            <meta name="format-detection" content="telephone=no" />
            <link rel="apple-touch-icon" href={appIcon} />
            <link rel="apple-touch-startup-image" href={launchImg} />
            <title>DreamHack App</title>
            <meta property="og:description" content={description} />
            <meta name="description" content={description} />
            <meta property="og:image" content={metaImage} />
            <meta name="apple-itunes-app" content="app-id=1390494035" />
          </Helmet>
          <RavenErrorBoundary>
            <Switch>
              <Route exact path={match.path} component={Events} />
              <Route path={`${match.url}/download`} component={Download} />
              <Route component={!transparent && Events} />
            </Switch>
          </RavenErrorBoundary>
          <Offline />
          <Route path={`${match.url}/:subRoutes*/me`}>
            {(props) => (
              <AnimatedRoutes
                match={props.match}
                location={location}
                animationStyle="slideUp"
              >
                <AnimatedMe
                  path={`${match.url}/:subRoutes*/me`}
                  {...props}
                />
              </AnimatedRoutes>
            )}
          </Route>
          <Route path={`${match.url}/event/:shortcode`}>
            {(props) => (

              <AnimatedRoutes
                match={props.match}
                location={location}
                animationStyle="slideIn"
              >
                <AnimatedEvent path={`${match.url}/event/:shortcode?`} {...props} />
              </AnimatedRoutes>
            )}
          </Route>
        </div>
        <div className={styles.footer}>
          <Link to="/cookies">Privacy settings</Link>
          <Link to="/policy">Terms of service</Link>
        </div>
      </div>
    );
  }
}

App.propTypes = propTypes;
App.contextType = SocketContext;

const mapStateToProps = (state) => ({
  simulatedTime: state.getIn(['app', 'simulatedTime']),
  transparent: state.getIn(['app', 'transparent']),
});

export default withUser(connect(mapStateToProps)(App));
